/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect } from 'react';
import { myApplications as initialApplications } from '../data/jobs';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { buildDefaultChecklist } from '../utils/onboardingSteps';
import { fetchProgressBatch, createProgressForAssignment } from '../services/onboardingService';
import { checkQualifications } from '../utils/qualificationCheck';
import { syncCurrentStaffed } from '../adapters/jobAdapter';
import useOnboardingSteps from '../hooks/useOnboardingSteps';
import { safeQuery, requireOnline } from '../utils/safeSupabase';

const AppContext = createContext();

export function AppProvider({ children }) {
  const { user: authUser, loading: authLoading, effectiveUserId, effectiveOrgId, effectiveRole } = useAuth();
  const { steps: templateSteps } = useOnboardingSteps();
  const userId = effectiveUserId || authUser?.id || null;
  const orgId = effectiveOrgId || null;
  const shouldLoadAnnotatorState = !!authUser && effectiveRole === 'annotator' && !!userId;

  // Theme state — applies data-theme attribute to <html>. Persists to localStorage.
  // IMPORTANT: the setAttribute must live in a useEffect, not the render body,
  // or else every AppProvider re-render (triggered by the many state slices
  // below) clobbers the value that layout-level effects (AdminLayout /
  // AnnotatorLayout / ClientPortal) set to `admin-dark`. Previously this was
  // called unconditionally during render, which lost the race against those
  // layout effects and flipped admin/client dashboards back to light on
  // every re-render cascade.
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem('juuni_theme') || 'light'; } catch { return 'light'; }
  });
  // Claim the attribute ONLY once at mount, and only if nothing has set it
  // yet. Layout-level effects (AdminLayout, AnnotatorLayout, ClientPortal)
  // run right after this and force their own theme. Any further AppProvider
  // re-renders must not clobber that — otherwise StrictMode's double-mount
  // cycle + subsequent re-renders race the layout effect and light wins.
  // The explicit toggleTheme path below still works via setAttribute on the
  // exact click that flips the toggle.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const current = document.documentElement.getAttribute('data-theme');
    if (!current) document.documentElement.setAttribute('data-theme', theme);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleTheme = (newTheme) => {
    setTheme(newTheme);
    // Explicit toggle always wins, even over layout defaults.
    document.documentElement.setAttribute('data-theme', newTheme);
    try { localStorage.setItem('juuni_theme', newTheme); } catch { /* intentionally empty */ }
  };

  // === Job state (shared across Jobs, ForYou, JobDetail, Profile) ===
  // Initialize empty — will be populated from Supabase on mount
  const [appliedJobs, setAppliedJobs] = useState(new Set());
  const [acceptedJobs, setAcceptedJobs] = useState(new Set());
  const [startedJobs, setStartedJobs] = useState(new Set());
  const [savedJobs, setSavedJobs] = useState(new Set());
  const [applications, setApplications] = useState([]);
  const [_jobStateLoaded, setJobStateLoaded] = useState(false);
  const [onboardingChecklists, setOnboardingChecklists] = useState({});

  const clearJobState = () => {
    setAppliedJobs(new Set());
    setAcceptedJobs(new Set());
    setStartedJobs(new Set());
    setSavedJobs(new Set());
    setApplications([]);
    setOnboardingChecklists({});
  };

  // Load job state from Supabase on mount
  useEffect(() => {
    if (authLoading) return;

    if (!shouldLoadAnnotatorState) {
      clearJobState();
      setJobStateLoaded(true);
      return;
    }

    async function loadJobState() {
      try {
        // Fetch applications, assignments, and saved jobs in parallel
        const [appsRes, assignmentsRes, savedRes] = await Promise.all([
          supabase.from('application').select('job_id, stage, decision, applied_at, estimated_response_time, job:job_id(title, domain, tier, base_rate, client_display_name, team_size, current_staffed, overview)').eq('applicant_id', userId),
          supabase.from('assignment').select('id, job_id, status, pay_confirmed, confirmed_rate, rate').eq('individual_id', userId),
          supabase.from('individual').select('saved_job_ids').eq('id', userId).single(),
        ]);

        if (appsRes.data) {
          const applied = new Set(appsRes.data.map(a => a.job_id));
          const accepted = new Set(appsRes.data.filter(a => a.stage === 'Accepted').map(a => a.job_id));
          setAppliedJobs(applied);
          setAcceptedJobs(accepted);

          // Transform applications to frontend format
          const transformedApps = appsRes.data.map(a => ({
            id: a.job_id,
            jobId: a.job_id,
            jobTitle: a.job?.title || '',
            domain: a.job?.domain || '',
            tier: a.job?.tier || 'Standard',
            rate: a.job?.base_rate || 0,
            client: a.job?.client_display_name || '',
            teamSize: a.job?.team_size || 0,
            currentStaffed: a.job?.current_staffed || 0,
            overview: a.job?.overview || '',
            appliedDate: new Date(a.applied_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
            estimatedResponse: a.estimated_response_time,
            stage: a.stage === 'Under Review' ? 'under_review' : a.stage === 'Not Selected' ? 'not_selected' : a.stage?.toLowerCase(),
            stageLabel: a.stage,
            decision: a.decision,
          }));
          setApplications(transformedApps);
        }

        if (assignmentsRes.data) {
          // Only truly Active assignments (onboarding complete) go in startedJobs
          const started = new Set(assignmentsRes.data.filter(a => a.status === 'Active').map(a => a.job_id));
          setStartedJobs(started);
          // Onboarding assignments go in acceptedJobs (visible but not working yet)
          const onboarding = new Set(assignmentsRes.data.filter(a => a.status === 'Onboarding').map(a => a.job_id));
          onboarding.forEach(id => setAcceptedJobs(prev => new Set([...prev, id])));

          // Fetch onboarding checklists for all assignments
          const assignmentIds = assignmentsRes.data.map(a => a.id).filter(Boolean);
          if (assignmentIds.length > 0) {
            const assignmentByJob = {};
            assignmentsRes.data.forEach(a => { assignmentByJob[a.job_id] = a; });

            // Fetch progress via new service (falls back to old table)
            const progressMap = await fetchProgressBatch(assignmentIds);

            const obMap = {};
            for (const assignment of assignmentsRes.data) {
              const progress = progressMap[assignment.id];
              if (progress && progress.rows.length > 0) {
                // Translate to flat format for backward compat with Jobs.jsx
                const flatObj = { assignment_id: assignment.id, job_id: assignment.job_id, _rows: progress.rows, _source: progress.source };
                for (const row of progress.rows) {
                  const legacyKey = row._legacy_key || row.step_id;
                  flatObj[legacyKey] = row.status;
                }
                flatObj.assignmentStatus = assignment.status;
                flatObj.pay_confirmed = assignment.pay_confirmed || false;
                // Phase 2 cross-phase sweep: removed dead `confirmed_rate`
                // field from the onboardingChecklists state map. Was written
                // here (and at 3 other sites) but never read anywhere —
                // grep `onboardingChecklists[*].confirmed_rate` returns only
                // writer sites. Dead state-map noise.
                obMap[assignment.job_id] = flatObj;
              } else if (assignment.status === 'Onboarding') {
                // Placeholder for assignments without any onboarding data
                obMap[assignment.job_id] = {
                  assignment_id: assignment.id,
                  job_id: assignment.job_id,
                  assignmentStatus: assignment.status,
                  pay_confirmed: assignment.pay_confirmed || false,
                  // Phase 2 cross-phase sweep: removed dead confirmed_rate (see above).
                };
              }
            }
            setOnboardingChecklists(obMap);
          }
        }

        if (savedRes.data?.saved_job_ids) {
          setSavedJobs(new Set(savedRes.data.saved_job_ids));
        }

      } catch (err) {
        console.warn('⚠️ Failed to load job state from Supabase, using fallback:', err);
        // Fallback to hardcoded IDs only in development for the annotator shell
        if (import.meta.env.DEV) {
          setAppliedJobs(new Set([1, 3, 501, 101]));
          setStartedJobs(new Set([1]));
          setSavedJobs(new Set([2, 502]));
          setApplications(initialApplications);
        } else {
          clearJobState();
        }
      } finally {
        setJobStateLoaded(true);
      }
    }
    loadJobState();
  }, [authLoading, orgId, shouldLoadAnnotatorState, userId]);

  const applyToJob = async (jobId, jobData) => {
    if (!requireOnline('applyToJob')) return { error: 'You are offline.' };
    if (!shouldLoadAnnotatorState || !userId) {
      return { error: 'Job actions are only available to signed-in annotators.' };
    }

    // Duplicate check — prevent double applications
    const { data: existingApp } = await safeQuery(supabase.from('application')
      .select('id').eq('applicant_id', userId).eq('job_id', jobId).limit(1), 'appContext-applyDuplicateCheck');
    if (existingApp && existingApp.length > 0) {
      console.warn('Application already exists for this job');
      setAppliedJobs(prev => new Set([...prev, jobId]));
      return;
    }

    // Optimistic UI update
    setAppliedJobs(prev => new Set([...prev, jobId]));
    const newApp = {
      id: `app-${Date.now()}`,
      jobId,
      jobTitle: jobData?.title || 'Unknown Job',
      domain: jobData?.domain || 'Audio',
      tier: jobData?.tier || 'Standard',
      rate: jobData?.rate || 0,
      appliedDate: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      stage: 'under_review',
      stageLabel: 'Under Review',
      estimatedResponse: '2-3 days',
      client: jobData?.client || '',
    };
    setApplications(prev => [...prev, newApp]);

    // Check qualifications against THIS job's domain (cross-domain + expiration aware)
    const [{ data: assessmentData }, { data: userData }] = await Promise.all([
      safeQuery(supabase.from('assessment_result')
        .select('assessment_id, passed, score, expires_at, assessment:assessment_id(name, type, domain)')
        .eq('user_id', userId)
        .eq('passed', true), 'appContext-applyAssessments'),
      safeQuery(supabase.from('individual')
        .select('resume_url, work_auth_status')
        .eq('id', userId)
        .single(), 'appContext-applyUserProfile'),
    ]);

    const userAssessments = (assessmentData || []).map(r => ({
      assessmentId: r.assessment_id,
      status: r.passed ? 'passed' : 'available',
      type: r.assessment?.type?.toLowerCase() || '',
      domain: r.assessment?.domain || null,
      expiresAt: r.expires_at,
    }));

    const qualification = checkQualifications({
      job: jobData,
      user: userData || {},
      assessments: userAssessments,
      requiredAssessments: jobData?.requiredAssessments || [],
    });

    // Write to Supabase with REAL per-job qualification data
    const { error } = await supabase.from('application').insert({
      job_id: jobId,
      applicant_id: userId,
      stage: 'Under Review',
      source: qualification.qualified ? 'QuickApply' : 'StandardApply',
      resume_submitted: qualification.steps.resume,
      domain_assessment_passed: qualification.steps.domainAssessment,
      skills_evaluation_passed: qualification.steps.skillsEvaluation,
      work_auth_verified: qualification.steps.workAuth,
      completed_steps: qualification.completedCount,
      estimated_response_time: '2-3 days',
      org_id: orgId,
    });

    if (error) {
      console.error('Failed to submit application to DB:', JSON.stringify(error));
      // Rollback optimistic UI update
      setAppliedJobs(prev => { const next = new Set(prev); next.delete(jobId); return next; });
      setApplications(prev => prev.filter(a => a.id !== newApp.id));
      // Surface error to user via global event
      window.dispatchEvent(new CustomEvent('juuni:error', { detail: { message: 'Failed to submit application. Please try again.' } }));
      return { error: error.message };
    }
  };

  // Start Now — for Instant Match jobs, create application + assignment in Onboarding status
  // Validates ALL qualifications before proceeding (domain assessment, skills eval, resume, work auth)
  const startJob = async (jobId, jobData) => {
    if (!requireOnline('startJob')) return { error: 'You are offline.' };
    if (!shouldLoadAnnotatorState || !userId) {
      return { error: 'Job actions are only available to signed-in annotators.' };
    }

    // Idempotency: check for existing assignment
    const { data: existingAssign } = await safeQuery(supabase.from('assignment')
      .select('id').eq('individual_id', userId).eq('job_id', jobId)
      .in('status', ['Onboarding', 'Active']).limit(1), 'appContext-startJobIdempotency');
    if (existingAssign && existingAssign.length > 0) {
      setAppliedJobs(prev => new Set([...prev, jobId]));
      setStartedJobs(prev => new Set([...prev, jobId]));
      return;
    }

    // ── Qualification Gate ──────────────────────────────
    // Fetch real assessment data + user profile from DB
    const [{ data: assessmentData }, { data: userData }] = await Promise.all([
      safeQuery(supabase.from('assessment_result')
        .select('assessment_id, passed, score, expires_at, assessment:assessment_id(name, type, domain)')
        .eq('user_id', userId)
        .eq('passed', true), 'appContext-startJobAssessments'),
      safeQuery(supabase.from('individual')
        .select('resume_url, work_auth_status')
        .eq('id', userId)
        .single(), 'appContext-startJobUserProfile'),
    ]);

    // Transform assessment data to match the shape checkQualifications expects
    const userAssessments = (assessmentData || []).map(r => ({
      assessmentId: r.assessment_id,
      status: r.passed ? 'passed' : 'available',
      type: r.assessment?.type?.toLowerCase() || '',
      domain: r.assessment?.domain || null,
      expiresAt: r.expires_at,
    }));

    const qualification = checkQualifications({
      job: jobData,
      user: userData || {},
      assessments: userAssessments,
      requiredAssessments: jobData?.requiredAssessments || [],
    });

    if (!qualification.canStartNow) {
      console.warn(`Cannot start job: missing ${qualification.missing.join(', ')}`);
      // Still let them apply normally if they have domain assessment
      if (qualification.canQuickApply) {
        return applyToJob(jobId, jobData);
      }
      return { error: `Missing qualifications: ${qualification.missing.join(', ')}` };
    }
    // ── End Qualification Gate ──────────────────────────

    setAppliedJobs(prev => new Set([...prev, jobId]));
    setStartedJobs(prev => new Set([...prev, jobId]));
    setAcceptedJobs(prev => new Set([...prev, jobId]));
    const newApp = {
      id: `app-${Date.now()}`,
      jobId,
      jobTitle: jobData?.title || 'Unknown Job',
      domain: jobData?.domain || 'Audio',
      tier: jobData?.tier || 'Standard',
      rate: jobData?.rate || 0,
      appliedDate: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      stage: 'accepted',
      stageLabel: 'Accepted — Onboarding',
      client: jobData?.client || '',
    };
    setApplications(prev => [...prev, newApp]);

    // Write application to DB as Accepted — with REAL qualification data
    const { data: appData, error: appError } = await supabase.from('application').insert({
      job_id: jobId,
      applicant_id: userId,
      stage: 'Accepted',
      source: 'InstantMatch',
      resume_submitted: qualification.steps.resume,
      domain_assessment_passed: qualification.steps.domainAssessment,
      skills_evaluation_passed: qualification.steps.skillsEvaluation,
      work_auth_verified: qualification.steps.workAuth,
      completed_steps: qualification.completedCount,
      decision: 'Accepted',
      org_id: orgId,
    }).select().single();

    if (appError) {
      console.error('Failed to create application:', appError);
      return;
    }
    console.log('✅ Instant Match application created');

    // Create assignment in Onboarding status (NOT Active — must complete onboarding first)
    // Phase 2 cross-phase sweep (R11 MED-4 audit anchor): Instant-Match
    // annotator-initiated WRITE path. `rate` is seeded from the job's
    // posting rate at creation; SPL may later confirm a different rate via
    // the pay-confirmation flow. `jobData` is post-adapter (transformJob
    // emits `.rate`), so the legacy `base_rate` / `baseRate` fallbacks
    // were dead branches. Unexplained literal `42` was a pre-adapter-era
    // magic number — replaced with `?? 0` (legit-zero preserved, no fake
    // default). Downstream pay-confirmation flow is the canonical source
    // of truth; this seed is deliberately explicit.
    const { data: assignData, error: assignError } = await supabase.from('assignment').insert({
      individual_id: userId,
      job_id: jobId,
      application_id: appData?.id || null,
      status: 'Onboarding',
      rate: jobData?.rate ?? 0,
      org_id: orgId,
    }).select().single();

    if (assignError) {
      console.error('Failed to create assignment:', assignError);
      return;
    }
    syncCurrentStaffed(jobId); // Keep job.current_staffed accurate

    // Auto-create onboarding (dual-write: old + new)
    const { error: obError } = await supabase.from('onboarding_checklist').insert({
      assignment_id: assignData.id, individual_id: userId, job_id: jobId, org_id: orgId,
    });
    // Also create progress rows in new table (non-blocking)
    createProgressForAssignment({
      assignmentId: assignData.id, individualId: userId, jobId, orgId,
      templateSteps,
    }).catch(() => {}); // Silently fail if no template exists yet

    if (obError) {
      console.error('Failed to create onboarding checklist:', obError);
    } else {
      console.log('✅ Onboarding checklist created');
      // Refresh onboarding data
      setOnboardingChecklists(prev => ({
        ...prev,
        [jobId]: {
          assignment_id: assignData.id, individual_id: userId, job_id: jobId,
          ...buildDefaultChecklist(),
          assignmentStatus: 'Onboarding',
          pay_confirmed: false,
          // Phase 2 cross-phase sweep: removed dead `confirmed_rate` field
          // (see identical fix ~line 146). Nothing reads it. Dead state-map noise.
          job: { uses_pipelines: jobData?.uses_pipelines !== false },
        },
      }));
    }
  };

  const startAcceptedJob = async (jobId) => {
    if (!requireOnline('startAcceptedJob')) return { error: 'You are offline.' };
    if (!shouldLoadAnnotatorState || !userId) {
      return { error: 'Job actions are only available to signed-in annotators.' };
    }

    // Prevent duplicate assignments
    const { data: existing } = await safeQuery(supabase.from('assignment')
      .select('id').eq('individual_id', userId).eq('job_id', jobId)
      .in('status', ['Onboarding', 'Active']).limit(1), 'appContext-startAcceptedDuplicateCheck');
    if (existing && existing.length > 0) {
      console.warn('Assignment already exists for this job');
      setStartedJobs(prev => new Set([...prev, jobId]));
      return;
    }

    setStartedJobs(prev => new Set([...prev, jobId]));
    setAcceptedJobs(prev => {
      const next = new Set(prev);
      next.delete(jobId);
      return next;
    });
    setApplications(prev => prev.map(app =>
      app.jobId === jobId
        ? { ...app, stage: 'accepted', stageLabel: 'Accepted — Onboarding' }
        : app
    ));

    // Update application stage in DB and preserve the lineage id for the downstream assignment.
    const { data: acceptedApplication, error: applicationError } = await supabase
      .from('application')
      .update({ stage: 'Accepted' })
      .eq('job_id', jobId)
      .eq('applicant_id', userId)
      .select('id')
      .single();

    if (applicationError) {
      console.error('Failed to update accepted application:', applicationError);
      return;
    }

    // Get job info for assignment (including environment)
    const { data: jobInfo } = await safeQuery(supabase.from('job').select('base_rate, uses_pipelines, environment').eq('id', jobId).single(), 'appContext-startAcceptedJobInfo');

    // Create assignment in Onboarding status (inherits environment from job)
    // Phase 2 cross-phase sweep (R11 MED-4 audit anchor): accepted-application
    // WRITE path. `jobInfo` is a raw DB query (line above selects snake_case
    // `base_rate`), so `.base_rate` is the correct field. Unexplained literal
    // `42` was a magic-number seed — replaced with `?? 0`, matching the
    // Instant-Match sibling path above. Pay-confirmation flow is the
    // canonical source of truth for the final assignment rate.
    const { data: assignData, error } = await supabase.from('assignment').insert({
      individual_id: userId,
      job_id: jobId,
      application_id: acceptedApplication?.id || null,
      status: 'Onboarding',
      rate: jobInfo?.base_rate ?? 0,
      environment: jobInfo?.environment || 'test',
      org_id: orgId,
    }).select().single();

    if (error) {
      console.error('Failed to create assignment:', error);
      return;
    }
    syncCurrentStaffed(jobId);

    // Auto-create onboarding (dual-write: old + new)
    const { error: obError } = await supabase.from('onboarding_checklist').insert({
      assignment_id: assignData.id, individual_id: userId, job_id: jobId, org_id: orgId,
    });
    createProgressForAssignment({
      assignmentId: assignData.id, individualId: userId, jobId, orgId,
      templateSteps,
    }).catch(() => {});

    if (obError) console.error('Failed to create onboarding checklist:', obError);
    else {
      console.log('✅ Onboarding checklist created');
      setOnboardingChecklists(prev => ({
        ...prev,
        [jobId]: {
          assignment_id: assignData.id, individual_id: userId, job_id: jobId,
          ...buildDefaultChecklist(),
          assignmentStatus: 'Onboarding',
          pay_confirmed: false,
          // Phase 2 cross-phase sweep: removed dead `confirmed_rate` field
          // (see identical fix ~line 146). Nothing reads it.
          job: { uses_pipelines: jobInfo?.uses_pipelines !== false },
        },
      }));
    }
  };

  const toggleSaveJob = async (jobId) => {
    if (!shouldLoadAnnotatorState || !userId) return;

    // Optimistic update — update local state immediately, rollback on failure
    const wasSaved = savedJobs.has(jobId);
    setSavedJobs(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });

    // Write to Supabase in background
    const newIds = wasSaved
      ? [...savedJobs].filter(id => id !== jobId)
      : [...savedJobs, jobId];
    const { error } = await supabase.from('individual')
      .update({ saved_job_ids: newIds })
      .eq('id', userId);
    if (error) {
      console.error('Failed to sync saved jobs to DB:', error);
      // Rollback optimistic update
      setSavedJobs(prev => {
        const reverted = new Set(prev);
        if (wasSaved) reverted.add(jobId);
        else reverted.delete(jobId);
        return reverted;
      });
    }
  };
  // Reward activation state: Set of active boost types (multiple can be active)
  const [activeBoosts, setActiveBoosts] = useState(new Set(['priority_access']));
  const [boostJustActivated, setBoostJustActivated] = useState(null);
  const [boostError, setBoostError] = useState(null);

  const activateReward = (type) => {
    if (activeBoosts.has(type)) {
      setBoostError('This reward is already active');
      setTimeout(() => setBoostError(null), 2500);
      return;
    }
    setActiveBoosts((prev) => new Set([...prev, type]));
    setBoostJustActivated(type);
    setBoostError(null);
    setTimeout(() => setBoostJustActivated(null), 3000);
  };

  return (
    <AppContext.Provider
      value={{
        theme,
        toggleTheme,
        activeBoosts,
        activateReward,
        boostJustActivated,
        boostError,
        // Job state
        appliedJobs,
        acceptedJobs,
        startedJobs,
        startAcceptedJob,
        savedJobs,
        applications,
        applyToJob,
        startJob,
        toggleSaveJob,
        onboardingChecklists,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
